import { Key, useEffect, useMemo, useState } from "react";
import { useLocale } from "react-aria";
import { useTranslation } from "react-i18next";
import { Item } from "react-stately";
import { DateFormatter } from "@internationalized/date";
import clsx from "clsx";

import { ChangeWeekLink } from "./ChangeWeekLink";
import { useTimetableContext, useTimetableData } from "./Timetable.hooks";

import { Button } from "~/components/Button";
import { SearchAutocomplete } from "~/components/Form/Autocomplete/SearchAutocomplete";
import { ChevronIcon, DIRECTION } from "~/components/Icons/ChevronIcon";
import { ErrorIcon } from "~/components/Icons/ErrorIcon";
import { GlobeIcon } from "~/components/Icons/GlobeIcon";
import { PencilIcon } from "~/components/Icons/PencilIcon";
import { Text } from "~/components/Text";
import { useStateManager } from "~/modules/StateManager.hooks";
import { useTimezoneUpdater } from "~/routes/$enterprise+/plugin+/$sessionId.api.timetable-timezone";

interface TimetableHeaderProps extends Pick<ReturnType<typeof useTimetableData>, "period"> {
  children: React.ReactNode;
  isMobile?: boolean;
  hasError?: boolean;
}

export function TimetableHeader({ children, hasError, isMobile, period }: TimetableHeaderProps) {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col" data-component="TimetableHeader">
      <div className="flex flex-col items-center pb-sm">
        <div className="flex w-full items-center" data-component="TimetableHeaderNavigation">
          <div className="md:w-28">
            <ChangeWeekLink aria-label={t("steps.Timetable.actions.previous_week")} type="previous">
              <ChevronIcon direction={DIRECTION.LEFT} />
              {!isMobile && (
                <span className="text-sm">
                  <Text i18nKey="steps.Timetable.actions.previous_week" />
                </span>
              )}
            </ChangeWeekLink>
          </div>

          <div
            className="flex flex-1 flex-col items-center text-xl font-bold"
            data-component="TimetableHeaderPeriod"
          >
            {period}
          </div>
          <div className="md:w-28 flex justify-end">
            <ChangeWeekLink aria-label={t("steps.Timetable.actions.next_week")} type="next">
              {!isMobile && (
                <span className="text-sm">
                  <Text i18nKey="steps.Timetable.actions.next_week" />
                </span>
              )}
              <ChevronIcon direction={DIRECTION.RIGHT} />
            </ChangeWeekLink>
          </div>
        </div>
        <TimetableTimeZone />
      </div>

      {hasError && (
        <div className={clsx("flex flex-row items-center justify-center gap-xs", "text-error-700")}>
          <ErrorIcon className="fill-error-700" />
          <Text i18nKey="util.validation.time_slot_not_available" />
        </div>
      )}
      <div className="flex rounded-lg border bg-white py-sm" data-component="TimetableHeaderWeek">
        {children}
      </div>
    </div>
  );
}

interface FormatInTimeZoneArgs {
  timeZone?: string;
  locale: string;
  date: Date;
}

function formatInTimeZone({ date, locale, timeZone }: FormatInTimeZoneArgs) {
  try {
    return new DateFormatter(locale, { hour: "numeric", minute: "numeric", timeZone }).format(date);
  } catch (err) {
    return null;
  }
}

export function TimetableTimeZone() {
  const stateManager = useStateManager();
  const { TIME_ZONES, timeZone } = useTimetableContext();
  const { locale } = useLocale();
  const { t } = useTranslation();
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    let intervalId = window.setInterval(() => setNow(new Date()), 1000 * 20);

    return () => window.clearInterval(intervalId);
  }, []);

  const timeZones = useMemo(
    () =>
      TIME_ZONES.map((timeZone) => ({
        timeZone,
        label: formatInTimeZone({ timeZone, locale, date: now }),
      })).filter((timeZone) => timeZone.label !== null),
    [now, locale, TIME_ZONES],
  );

  const nowInTimeZone = useMemo(
    () =>
      formatInTimeZone({ date: now, timeZone, locale }) ??
      formatInTimeZone({ date: now, locale }) ??
      "",
    [now, timeZone, locale],
  );

  const [isEditTimeZone, setIsEditTimezone] = useState(false);

  const enableTimeZoneSelection =
    stateManager.state.config.application.timezoneSelection === "enable_timezone_selection";

  const updateTimeZone = useTimezoneUpdater();
  function handleTimeZoneChange(timeZone: Key) {
    if (timeZone) {
      setIsEditTimezone(false);

      updateTimeZone(timeZone.toString());
    }
  }

  if (enableTimeZoneSelection && isEditTimeZone) {
    return (
      <div className="flex w-[300px] pt-xs">
        <SearchAutocomplete
          addonLeft={<TimeZoneIcon />}
          aria-label={t("steps.Timetable.TimezoneSelect.label")}
          data-component="TimeZoneSelector"
          defaultItems={timeZones}
          maxHeight="max-h-[200px]"
          onSelectionChange={handleTimeZoneChange}
          placeholder={t("steps.Timetable.TimezoneSelect.placeholder")}
          selectedKey={timeZone}
        >
          {(item) => (
            <Item key={item.timeZone} textValue={item.timeZone}>
              <div className="flex w-full items-center justify-between">
                <span>{item.timeZone}</span>
                <span className="text-sm">{item.label}</span>
              </div>
            </Item>
          )}
        </SearchAutocomplete>
      </div>
    );
  }

  return (
    <div
      className="flex h-[48px] items-center justify-center gap-xs py-xs text-sm text-gray-500"
      data-component="TimeZoneSelectorDisplay"
    >
      <TimeZoneIcon />
      <span data-component="TimetableTimeZoneLabel">
        {timeZone} ({nowInTimeZone})
      </span>
      {enableTimeZoneSelection && (
        <Button onPress={() => setIsEditTimezone(true)}>
          <PencilIcon
            className="h-sm w-sm shrink-0 text-gray-500"
            data-component="TimetableTimeZoneEditIcon"
          />
        </Button>
      )}
    </div>
  );
}

function TimeZoneIcon() {
  return (
    <GlobeIcon
      aria-hidden="true"
      className="h-sm w-sm shrink-0 text-gray-500"
      data-component="TimetableTimeZoneIcon"
    />
  );
}
